import { Alert, AlertTitle, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { deviceCampaignLabel, deviceCampaignPriceNumber, devicePriceNote, devicePriceNumber, noreplyEmail } from 'src/config'

export type HelpContent = {
  id: string
  title: string
  summary: string
  content: (clientId?: string) => ReactNode
}

/**
 * clientId があればログイン済み（かつクライアント選択済み）。
 * 各記事は「未ログインなら何から始めるか」を案内し、ログイン済みなら実際のリンクを出す。
 */
export const helpContents: HelpContent[] = [
  {
    id: 'getting-started',
    title: '利用開始のやり方',
    summary: '利用申請から、デバイスを使い始めるまでの流れ',
    content: (clientId?: string) => (
      <Stack spacing={3}>
        <Typography>ワナップは利用申請を承認させていただいた方からご利用いただけます。申請からご利用開始までの流れは次の通りです。</Typography>

        <Stack spacing={1}>
          <Typography variant="h6">1. 利用申請する</Typography>
          <Typography>
            <Link to="/inquiry/register">利用申請フォーム</Link>
            からお申し込みください。お名前・メールアドレスなどをご入力いただきます。
          </Typography>
          <Typography>招待コードをお持ちの場合は、</Typography>
          <Typography>
            <Link to="/invitation">招待コード付きの申請フォーム</Link>
            からお申し込みいただくと、その場で登録が完了します。
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">2. 承認を待つ</Typography>
          <Typography>
            運営で内容を確認し、承認されると {noreplyEmail || '運営'} よりメールで通知が届きます。このメールが届いてからログインが可能になります。
          </Typography>
          <Alert severity="info">
            申請済みのメールアドレスで再度申請することはできません。承認の連絡が届かない場合は、お手数ですが運営までお問い合わせください。
          </Alert>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">3. ログインする</Typography>
          <Typography>
            承認メールが届いたら <Link to="/auth/login">ログインページ</Link>{' '}
            からログインしてください。ログイン方法は、メールアドレスを入力してログイン用リンクを受け取る方法と、Googleアカウントでログインする方法の2通りがあります。
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">4. デバイスを購入する</Typography>
          <Typography>
            ログイン後、デバイスをご購入いただきます。手順は <Link to="/help/purchase">デバイスの購入方法</Link> をご覧ください。
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">5. 届いたデバイスを登録する</Typography>
          <Typography>
            デバイスが届いたら、同梱のご案内に従ってデバイスを登録してください。
            {clientId ? (
              <>
                {' '}
                登録後は <Link to={`/clients/${clientId}/devices`}>デバイス一覧</Link> に表示され、作動状況を確認できるようになります。
              </>
            ) : (
              ' 登録後はデバイス一覧に表示され、作動状況を確認できるようになります。'
            )}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">6. 通知を設定する</Typography>
          <Typography>
            罠が作動したときに気づけるよう、通知の設定をおすすめします。設定方法は <Link to="/help/line">LINE通知の設定方法</Link> をご覧ください。
          </Typography>
        </Stack>
      </Stack>
    ),
  },
  {
    id: 'purchase',
    title: 'デバイスの購入方法',
    summary: 'デバイスの注文からお支払い、発送までの流れ',
    content: (clientId?: string) => (
      <Stack spacing={3}>
        {clientId ? (
          <Typography>
            <Link to={`/clients/${clientId}/orders`}>デバイスの購入ページ</Link>
            からご注文いただけます。
          </Typography>
        ) : (
          <Alert severity="info">
            デバイスのご購入にはログインが必要です。まだアカウントをお持ちでない方は <Link to="/help/getting-started">利用開始のやり方</Link> をご覧ください。
          </Alert>
        )}

        <Stack spacing={1}>
          <Typography variant="h6">1. 購入ページを開く</Typography>
          <Typography>
            {clientId ? (
              <>
                左のメニューから「デバイスの購入」、または <Link to={`/clients/${clientId}/orders`}>こちら</Link> を開きます。
              </>
            ) : (
              'ログイン後、左のメニューから「デバイスの購入」を開きます。'
            )}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">2. 数量と送付先を入力する</Typography>
          <Typography>ご希望のデバイスを選び、数量と送付先をご入力ください。送付先は過去にご利用いただいた住所から選ぶこともできます。</Typography>
          <Typography>数量は在庫数までご指定いただけます。SIMが必要なモデルの場合は、あわせてSIMプランをお選びください。</Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">3. お支払い</Typography>
          <Typography>
            ご注文いただくと請求書が発行されます。<strong>お支払い期限は発行から7日間</strong>です。送料は注文内容に応じて加算されます。
          </Typography>
          <Typography>
            デバイス本体の価格は {devicePriceNumber} 円（税込）です。{deviceCampaignLabel}適用時は {deviceCampaignPriceNumber} 円（税込）となります。
            {devicePriceNote}
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">4. 発送</Typography>
          <Typography>
            お支払いの確認後、順次発送いたします。
            {clientId ? (
              <>
                {' '}
                発送状況は <Link to={`/clients/${clientId}/orders`}>購入ページの注文履歴</Link> からご確認いただけます。
              </>
            ) : (
              ' 発送状況は購入ページの注文履歴からご確認いただけます。'
            )}
          </Typography>
        </Stack>

        <Alert severity="info">
          <Typography>
            在庫がない場合や大量にご注文いただく場合は受注生産となります。
            {clientId ? (
              <>
                {' '}
                <Link to={`/clients/${clientId}/inquiries`}>お問い合わせ</Link> よりご相談ください。
              </>
            ) : (
              ' ログイン後、お問い合わせよりご相談ください。'
            )}
          </Typography>
        </Alert>
      </Stack>
    ),
  },
  {
    id: 'line',
    title: 'LINE通知の設定方法',
    summary: '罠の作動をLINEで受け取る設定',
    content: (clientId?: string) => (
      <Stack spacing={3}>
        <Typography>
          罠が作動したときに、LINEで通知を受け取れるように設定します。通知先は「個人」「グループ全体」「サブグループ」の3種類から選べます。
        </Typography>

        <Alert severity="info">
          「グループ全体」と「サブグループ」の通知先は、<strong>ご利用形態がグループの場合のみ</strong>設定できます。
          個人（ユーザー）でご利用の場合は「個人で受け取る」の設定をお使いください。
        </Alert>

        <Alert severity="info">
          <AlertTitle>設定する場所と連携コードは通知先ごとに違います</AlertTitle>
          どの通知先も「公式LINEを招待する」→「その画面に表示された連携コードをトークルームに送信する」という流れは共通ですが、
          <strong>連携コードは通知先ごとに別のもの</strong>で、取得する画面も異なります。
        </Alert>

        <Stack spacing={1}>
          <Typography variant="h6">共通の準備: ワナップ公式LINEを招待する</Typography>
          <Typography>
            各設定画面に表示されるQRコードまたはリンクから、ワナップ公式アカウントを友だち追加してください。
            <br />
            グループやルームに通知を送りたい場合は、そのトークルームに公式アカウントを招待してください。
          </Typography>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">個人で受け取る</Typography>
          <Typography>
            自分宛ての通知を受け取る設定です。
            {clientId ? (
              <>
                {' '}
                右上のアイコンからユーザー名をクリックするか、 <Link to={`/clients/${clientId}/profile`}>プロフィール設定</Link> を開きます。
              </>
            ) : (
              ' ログインしたうえで、右上のアイコンからユーザー名をクリックしてプロフィール設定を開きます。'
            )}
          </Typography>
          <Typography>LINE通知をONにすると連携コードが表示されます。これをコピーして、通知を受け取りたいトークルームに送信してください。</Typography>
          <img style={{ border: '1px solid #3f3f3f' }} src="/helpimages/edit_profile1.png" width="100%" alt="プロフィール編集画面の右上アイコン" />
          <img style={{ border: '1px solid #3f3f3f' }} src="/helpimages/edit_profile2.png" width="100%" alt="ユーザー名の変更画面" />
          <img style={{ border: '1px solid #3f3f3f' }} src="/helpimages/edit_profile3.png" width="100%" alt="トークルームへのコード貼り付け例" />
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">グループ全体で受け取る</Typography>
          <Typography>
            そのグループのすべてのデバイスの通知を、ひとつのトークルームに送る設定です。
            {clientId ? (
              <>
                {' '}
                <Link to={`/clients/${clientId}`}>グループの画面</Link> の「LINE通知先の設定」から行います。
              </>
            ) : (
              ' ログイン後、グループの画面の「LINE通知先の設定」から行います。'
            )}
          </Typography>
          <Typography>
            この設定は<strong>グループでご利用の場合のみ</strong>、かつ<strong>グループの管理者のみ</strong>行えます。
            画面に表示された連携コードを、通知先にしたいトークルームに送信すると設定されます。
          </Typography>
          <Alert severity="warning">
            グループ全体の通知先は<strong>ひとつのトークルームだけ</strong>
            です。別のトークルームに変えたいときは、先に画面の「連携を解除する」から解除してください。
            解除せずに別のトークルームへコードを送ると、すでに設定済みであることが返信され、変更されません。
          </Alert>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">サブグループで受け取る</Typography>
          <Typography>
            サブグループに登録したデバイスの通知だけを、そのサブグループ専用のトークルームに送る設定です。
            {clientId ? (
              <>
                {' '}
                <Link to={`/clients/${clientId}/subGroups`}>サブグループ</Link> から対象のサブグループを開き、「LINE通知先の設定」から行います。
              </>
            ) : (
              ' ログイン後、サブグループの画面を開き、「LINE通知先の設定」から行います。'
            )}
          </Typography>
          <Alert severity="warning">
            サブグループは<strong>グループでご利用の場合のみ</strong>利用できます。さらに、サブグループ単位の通知は <strong>Proプラン</strong> の機能です。
            Proプランでない場合、サブグループに通知先を設定しても通知は送られません。設定・編集にはグループの管理者権限も必要です。
          </Alert>
        </Stack>

        <Stack spacing={1}>
          <Typography variant="h6">通知先を解除する</Typography>
          <Typography>解除する方法は2つあります。</Typography>
          <Typography>
            ・各設定画面の「連携を解除する」から解除する
            <br />
            ・そのトークルームからワナップ公式アカウントを退出させる、または公式アカウントをブロックする
          </Typography>
          <Typography>
            公式アカウントを退出・ブロックした場合は、そのトークルームに紐づいていた通知先（個人・グループ全体・サブグループ）がすべて解除されます。
          </Typography>
        </Stack>
      </Stack>
    ),
  },
]
